/**
 * useAdminCapabilities Hook
 *
 * Provides convenience methods for checking admin capabilities.
 * Wraps the auth store with admin-specific checks.
 */

import type { Capability, AdminCapability } from "@tatvex/types";
import { useMemo, useCallback } from "react";

import { useAuthStore } from "../store/authStore";

/** Admin capability check results */
export interface AdminCapabilities {
  /** Check if user has admin:* wildcard */
  readonly isSuperAdmin: boolean;

  /** Individual admin capability checks */
  readonly canManageUsers: boolean;
  readonly canManageRoles: boolean;
  readonly canManageTenants: boolean;
  readonly canViewAudit: boolean;
  readonly canViewHealth: boolean;

  /** Check if user has any admin capability */
  readonly hasAnyAdminCapability: boolean;

  /** Check if user has specific admin capability */
  readonly hasAdminCapability: (capability: AdminCapability) => boolean;

  /** Check if user has all specified capabilities */
  readonly hasAllCapabilities: (capabilities: readonly Capability[]) => boolean;

  /** Check if user has any of specified capabilities */
  readonly hasAnyCapability: (capabilities: readonly Capability[]) => boolean;

  /** All capabilities the user has */
  readonly capabilities: readonly Capability[];
}

/** All admin capabilities for checking */
const ADMIN_CAPABILITIES: readonly AdminCapability[] = [
  "admin:users",
  "admin:roles",
  "admin:tenants",
  "admin:audit",
  "admin:health",
];

/**
 * Hook for checking admin capabilities.
 */
export function useAdminCapabilities(): AdminCapabilities {
  const capabilities = useAuthStore((state) => state.capabilities);
  const hasCapability = useAuthStore((state) => state.hasCapability);
  const hasAllCapabilitiesFn = useAuthStore((state) => state.hasAllCapabilities);
  const hasAnyCapabilityFn = useAuthStore((state) => state.hasAnyCapability);

  const isSuperAdmin = useMemo(() => hasCapability("admin:*"), [hasCapability]);

  const canManageUsers = useMemo(
    () => isSuperAdmin || hasCapability("admin:users"),
    [isSuperAdmin, hasCapability]
  );

  const canManageRoles = useMemo(
    () => isSuperAdmin || hasCapability("admin:roles"),
    [isSuperAdmin, hasCapability]
  );

  const canManageTenants = useMemo(
    () => isSuperAdmin || hasCapability("admin:tenants"),
    [isSuperAdmin, hasCapability]
  );

  const canViewAudit = useMemo(
    () => isSuperAdmin || hasCapability("admin:audit"),
    [isSuperAdmin, hasCapability]
  );

  const canViewHealth = useMemo(
    () => isSuperAdmin || hasCapability("admin:health"),
    [isSuperAdmin, hasCapability]
  );

  const hasAnyAdminCapability = useMemo(
    () => isSuperAdmin || ADMIN_CAPABILITIES.some((cap) => capabilities.includes(cap)),
    [isSuperAdmin, capabilities]
  );

  const hasAdminCapability = useCallback(
    (capability: AdminCapability): boolean => {
      if (isSuperAdmin) return true;
      return hasCapability(capability);
    },
    [isSuperAdmin, hasCapability]
  );

  const hasAllCapabilities = useCallback(
    (caps: readonly Capability[]): boolean => {
      if (isSuperAdmin) return true;
      return hasAllCapabilitiesFn(caps);
    },
    [isSuperAdmin, hasAllCapabilitiesFn]
  );

  const hasAnyCapability = useCallback(
    (caps: readonly Capability[]): boolean => {
      if (isSuperAdmin) return true;
      return hasAnyCapabilityFn(caps);
    },
    [isSuperAdmin, hasAnyCapabilityFn]
  );

  return useMemo(
    () => ({
      isSuperAdmin,
      canManageUsers,
      canManageRoles,
      canManageTenants,
      canViewAudit,
      canViewHealth,
      hasAnyAdminCapability,
      hasAdminCapability,
      hasAllCapabilities,
      hasAnyCapability,
      capabilities,
    }),
    [
      isSuperAdmin,
      canManageUsers,
      canManageRoles,
      canManageTenants,
      canViewAudit,
      canViewHealth,
      hasAnyAdminCapability,
      hasAdminCapability,
      hasAllCapabilities,
      hasAnyCapability,
      capabilities,
    ]
  );
}

/**
 * Get admin navigation items based on capabilities.
 */
export interface AdminNavItem {
  readonly id: string;
  readonly label: string;
  readonly href: string;
  readonly capability: AdminCapability;
}

const ADMIN_NAV_ITEMS: readonly AdminNavItem[] = [
  { id: "tenants", label: "Tenants", href: "/studio/admin/tenants", capability: "admin:tenants" },
  { id: "users", label: "Users", href: "/studio/admin/users", capability: "admin:users" },
  { id: "roles", label: "Roles", href: "/studio/admin/roles", capability: "admin:roles" },
  { id: "audit", label: "Audit Logs", href: "/studio/admin/audit", capability: "admin:audit" },
  {
    id: "health",
    label: "System Health",
    href: "/studio/admin/health",
    capability: "admin:health",
  },
];

/**
 * Hook for getting admin navigation items based on capabilities.
 */
export function useAdminNavigation(): {
  readonly items: readonly AdminNavItem[];
  readonly hasAccess: boolean;
} {
  const { hasAdminCapability, hasAnyAdminCapability } = useAdminCapabilities();

  const items = useMemo(
    () => ADMIN_NAV_ITEMS.filter((item) => hasAdminCapability(item.capability)),
    [hasAdminCapability]
  );

  return useMemo(
    () => ({
      items,
      hasAccess: hasAnyAdminCapability,
    }),
    [items, hasAnyAdminCapability]
  );
}

export default useAdminCapabilities;
